import { useCallback, useMemo, useRef, useState } from 'react';
import type { ProspectLoan } from '../../types';
import type { LoanPricingItem } from '@/services/loanPricingService';
import { toNumberOrNull } from '../../utils/loanValueHelpers';
import { formatDollarInputValue } from '../inputFormat';

/**
 * Parse a user-typed numeric string tolerantly. Strips `$`, commas, whitespace
 * before delegating to toNumberOrNull. Returns null on blank or unparseable.
 * Rationale: users commonly paste/type "10,000" in dollar fields or copy from
 * other tools with trailing whitespace; strict Number('10,000') returns NaN and
 * silently drops the override (Finding #3 — 2026-04-21).
 */
function parseInputNumber(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (cleaned === '') {
    return null;
  }
  return toNumberOrNull(cleaned);
}

export type PricingFieldId =
  | 'discount_point_percent'
  | 'origination_fee_percent' // BPC — percent-native to match Encompass field 388
  | 'lender_credit_4794_dollars'
  | 'lo_comp_reduction_bps'       // LOCR
  | 'investor_loan_number';       // Encompass 352 — text, saved with the group, never priced
// Cure intentionally absent — deferred pending a Concessions & Cure modal + fee-selection context.

/** The four priced fields. The Investor Loan # is the draft's one text field. */
export type NumericPricingFieldId = Exclude<PricingFieldId, 'investor_loan_number'>;

/** Encompass 352 allowlist limit (encompass_writable_fields.max_length); the server re-checks. */
export const INVESTOR_LOAN_NUMBER_MAX_LENGTH = 50;

/**
 * Investor Loan # entry error. An untouched value (blank included) is never an
 * error, so a loan without a number can still save pricing; clearing an
 * existing number is refused, as the server refuses it.
 */
export function investorLoanNumberError(value: string, previous: string): string | null {
  const trimmed = value.trim();
  if (trimmed === previous.trim()) {
    return null;
  }
  if (trimmed === '') {
    return "Investor Loan # can't be blank";
  }
  if (trimmed.length > INVESTOR_LOAN_NUMBER_MAX_LENGTH) {
    return `Investor Loan # is limited to ${INVESTOR_LOAN_NUMBER_MAX_LENGTH} characters`;
  }
  return null;
}

export interface PricingSaveField {
  fieldId: string;
  value: string | null;
}

export interface PricingDraftValue {
  value: string;        // Raw input (empty = no override during preview, except lender credit where empty previews as 0)
  previous: string;     // Loan/pricing value at draft-open or last rebase (string form of the numeric)
  changed: boolean;     // value and previous differ numerically (NaN-safe; blank counts as "no value")
  invalid: string | null; // Validation error (e.g. BPC cap exceeded). Null when valid.
}

export interface UsePricingDraftOutput {
  values: Record<PricingFieldId, PricingDraftValue>;
  setValue: (id: PricingFieldId, raw: string) => void;
  setInvalid: (id: PricingFieldId, err: string | null) => void;
  discardAll: () => void;
  discardField: (id: PricingFieldId) => void;
  /** Re-captures current loan + pricingItems as the new `previous` for every field, clears dirty. */
  rebase: (nextLoan: ProspectLoan, nextPricingItems: LoanPricingItem[]) => void;
  /**
   * Rebases only unchanged fields to the new baseline. Dirty fields keep their value + invalid,
   * but their `previous` advances to the fresh baseline (and `changed` is recomputed against it).
   * Used by the async-hydration effect in the workspace: when `pricingItems` finally resolves,
   * we must pick up LOCR's true `reduction_bps` — even if the user has already edited another
   * field in the meantime.
   */
  rebaseClean: (nextLoan: ProspectLoan, nextPricingItems: LoanPricingItem[]) => void;
  /**
   * Replaces specific field values with caller-provided strings and resets them as the
   * new baseline (value = previous = override; changed = false; invalid = null). Fields
   * absent from the overrides map are left untouched. A field edited since the cancelled
   * save was accepted (still dirty, with a value other than its override) keeps that
   * value as a pending edit against the override.
   *
   * Used by the cancel flow: the server returns reverted values on ok:true, and the
   * workspace translates them into draft-unit strings (e.g. '388' percent -> bps) so the
   * aside inputs reflect the reverted state instead of the cancelled staged state.
   * `rebase(loan, pricingItems)` would read the parent's loan prop, which may not
   * yet include the server-side cancellation.
   */
  rebaseFromValues: (overrides: Partial<Record<PricingFieldId, string>>) => void;
  /**
   * Marks an accepted save's submitted values as the saved baseline. Only a field that
   * still holds exactly what was sent becomes clean; one typed while the save was on
   * the wire, including a return to its earlier value, stays a pending edit against
   * the saved value. Clears those fields' submission marks.
   */
  rebaseSubmitted: (submitted: Partial<Record<PricingFieldId, string>>) => void;
  /**
   * Marks the values a save is sending. Until `rebaseSubmitted` (accepted) or
   * `endSubmission` (not accepted), `rebaseClean` keeps these fields as typed: the
   * loan row can already hold the sent value (staging writes the Investor Loan #)
   * while the field shows a later entry, including a return to the earlier value.
   */
  beginSubmission: (submitted: Partial<Record<PricingFieldId, string>>) => void;
  /** Clears the submission marks after a save that was not accepted. */
  endSubmission: () => void;
  dirtyCount: number;
  dirtyFields: PricingFieldId[];
  /**
   * True while a save is sending and a field it sent now holds something else,
   * including its earlier value. That entry is not part of the save and can read
   * as unchanged against the old baseline until the reply, so leaving would lose it.
   */
  hasUnsentEdits: boolean;
  anyInvalid: boolean;
  /**
   * Numeric overrides for usePricingRecalc. Unchanged or blank values → undefined
   * (hook falls back to the loan value), except dirty blank lender credit → 0
   * so the live math matches the user's cleared input. BPC is already
   * percent-native, so usePricingRecalc's `bpcPercent` override receives the
   * typed value directly.
   */
  recalcOverrides: {
    discountPointPercent?: number | null;
    lenderCredit4794DollarsOverride?: number | null;
    locrBps?: number | null;
    bpcPercent?: number | null;
  };
  /**
   * Save payload for useGroupedPricingSave. Only DIRTY fields included.
   * Blank → null for clearable pricing fields.
   * Does NOT send previousLocalValue — server derives its own baselines from the
   * DB inside the staging RPC.
   */
  buildSavePayload: () => PricingSaveField[];
}

/** Canonical save-time fieldId for each draft key (used by the save payload). */
const SAVE_FIELD_ID: Record<PricingFieldId, string> = {
  discount_point_percent: 'NEWHUD.X1150',
  origination_fee_percent: '388',
  lender_credit_4794_dollars: '4794',
  lo_comp_reduction_bps: 'LW.LO_REDUCTION',
  investor_loan_number: '352'
};

/** Compared and saved as text: letters, case and leading zeros are the value. */
const TEXT_FIELD_IDS = new Set<PricingFieldId>(['investor_loan_number']);

function formatPercentInputValue(n: number): string {
  return n.toFixed(3).replace(/\.?0+$/, '');
}

export function formatBpsInputValue(n: number): string {
  const truncated = Math.trunc((n + Number.EPSILON) * 100) / 100;
  return truncated.toFixed(2).replace(/\.?0+$/, '');
}

function readInitialValue(id: PricingFieldId, loan: ProspectLoan, pricingItems: LoanPricingItem[]): string {
  switch (id) {
    case 'discount_point_percent': {
      const n = toNumberOrNull(loan.discount_point_percent);
      return n === null ? '' : n.toFixed(3);
    }
    case 'origination_fee_percent': {
      const n = toNumberOrNull(loan.origination_fee_percent);
      return n === null ? '' : formatPercentInputValue(n);
    }
    case 'lender_credit_4794_dollars': {
      const n = toNumberOrNull(loan.trans_details_non_specific_lender_credit);
      return n === null ? '' : formatDollarInputValue(n.toFixed(2));
    }
    case 'lo_comp_reduction_bps': {
      const item = pricingItems.find((i) => i.itemType === 'lo_compensation');
      const n = item ? toNumberOrNull(item.reductionBps) : null;
      return n === null ? '' : formatBpsInputValue(n);
    }
    case 'investor_loan_number':
      return typeof loan.rate_lock_investor_commitment === 'string'
        ? loan.rate_lock_investor_commitment.trim()
        : '';
  }
}

function buildInitial(loan: ProspectLoan, pricingItems: LoanPricingItem[]): Record<PricingFieldId, PricingDraftValue> {
  const ids: PricingFieldId[] = [
    'discount_point_percent',
    'origination_fee_percent',
    'lender_credit_4794_dollars',
    'lo_comp_reduction_bps',
    'investor_loan_number'
  ];
  const result = {} as Record<PricingFieldId, PricingDraftValue>;
  for (const id of ids) {
    const initial = readInitialValue(id, loan, pricingItems);
    result[id] = { value: initial, previous: initial, changed: false, invalid: null };
  }
  return result;
}

/** Numeric equality that treats blank/empty as "no value" consistently. NaN → null → equal.
 *  Uses parseInputNumber so formatted input like "10,000" compares correctly against "10000". */
function numericallyEqual(a: string, b: string): boolean {
  const na = parseInputNumber(a);
  const nb = parseInputNumber(b);
  return na === nb;
}

function draftValuesEqual(id: PricingFieldId, a: string, b: string): boolean {
  return TEXT_FIELD_IDS.has(id) ? a.trim() === b.trim() : numericallyEqual(a, b);
}

/**
 * Draft state for pricing-v2 aside edits.
 *
 * Per the Phase 2 plan (see `.claude/plans/go-to-loan-wondrous-castle.md` §2A.1):
 *   - Blank while typing = "no override" (live preview falls back to loan value),
 *     except lender credit where blank previews as $0.
 *   - Blank on Save = null for clearable pricing fields; LOCR blank remains invalid.
 *   - `previous` captured at mount / last rebase — stays stable across re-renders of `loan`.
 *   - `rebase`/`rebaseClean` are explicit sync points. The workspace may keep
 *     submitted values visible in a pending readback state, then rebase once
 *     authoritative loan/pricing rows refresh.
 */
export function usePricingDraft(loan: ProspectLoan, pricingItems: LoanPricingItem[]): UsePricingDraftOutput {
  // Initial values captured ONCE — we deliberately do not sync `values` when `loan` changes after
  // mount. Mid-edit the loan prop can re-render (snapshot select, realtime refresh) and we must
  // not stomp the user's in-progress input. `rebase` is the explicit re-sync hook.
  const initialRef = useRef<Record<PricingFieldId, PricingDraftValue>>();
  if (!initialRef.current) {
    initialRef.current = buildInitial(loan, pricingItems);
  }
  const [values, setValues] = useState<Record<PricingFieldId, PricingDraftValue>>(initialRef.current);

  const setValue = useCallback((id: PricingFieldId, raw: string) => {
    setValues((prev) => {
      const current = prev[id];
      const changed = !draftValuesEqual(id, raw, current.previous);
      return { ...prev, [id]: { ...current, value: raw, changed } };
    });
  }, []);

  const setInvalid = useCallback((id: PricingFieldId, err: string | null) => {
    setValues((prev) => {
      if (prev[id].invalid === err) {
        return prev;
      }
      return { ...prev, [id]: { ...prev[id], invalid: err } };
    });
  }, []);

  const discardField = useCallback((id: PricingFieldId) => {
    setValues((prev) => {
      const p = prev[id].previous;
      return { ...prev, [id]: { value: p, previous: p, changed: false, invalid: null } };
    });
  }, []);

  const discardAll = useCallback(() => {
    setValues((prev) => {
      const result = {} as Record<PricingFieldId, PricingDraftValue>;
      (Object.keys(prev) as PricingFieldId[]).forEach((id) => {
        const p = prev[id].previous;
        result[id] = { value: p, previous: p, changed: false, invalid: null };
      });
      return result;
    });
  }, []);

  const rebase = useCallback((nextLoan: ProspectLoan, nextPricingItems: LoanPricingItem[]) => {
    const next = buildInitial(nextLoan, nextPricingItems);
    initialRef.current = next;
    setValues(next);
  }, []);

  // Values a save is sending, until it is accepted or refused. rebaseClean reads
  // the ref when it is called; the state mirrors it for rendering.
  const submittingRef = useRef<Partial<Record<PricingFieldId, string>>>({});
  const [submitting, setSubmitting] = useState<Partial<Record<PricingFieldId, string>>>({});
  const setSubmissionMarks = useCallback((marks: Partial<Record<PricingFieldId, string>>) => {
    submittingRef.current = marks;
    setSubmitting(marks);
  }, []);

  const rebaseClean = useCallback((nextLoan: ProspectLoan, nextPricingItems: LoanPricingItem[]) => {
    // The marks as of this call: the save's reply may clear them before the
    // update below runs, and this rebase was asked for while they applied.
    const submitting = { ...submittingRef.current };
    setValues((prev) => {
      const next = buildInitial(nextLoan, nextPricingItems);
      const result = {} as Record<PricingFieldId, PricingDraftValue>;
      (Object.keys(prev) as PricingFieldId[]).forEach((id) => {
        if (prev[id].changed || submitting[id] !== undefined) {
          const newPrevious = next[id].previous;
          result[id] = {
            value: prev[id].value,
            previous: newPrevious,
            changed: !draftValuesEqual(id, prev[id].value, newPrevious),
            invalid: prev[id].invalid
          };
        } else {
          result[id] = next[id];
        }
      });
      initialRef.current = result;
      return result;
    });
  }, []);

  const rebaseFromValues = useCallback((overrides: Partial<Record<PricingFieldId, string>>) => {
    setValues((prev) => {
      const result = {} as Record<PricingFieldId, PricingDraftValue>;
      (Object.keys(prev) as PricingFieldId[]).forEach((id) => {
        const override = overrides[id];
        const current = prev[id];
        if (override === undefined) {
          result[id] = current;
        } else if (current.changed && !draftValuesEqual(id, current.value, override)) {
          // Edited since the cancelled save was accepted: keep the edit, pending
          // against the value the server restored.
          result[id] = { ...current, previous: override, changed: true };
        } else {
          result[id] = { value: override, previous: override, changed: false, invalid: null };
        }
      });
      initialRef.current = result;
      return result;
    });
  }, []);

  const beginSubmission = useCallback((submitted: Partial<Record<PricingFieldId, string>>) => {
    setSubmissionMarks({ ...submitted });
  }, [setSubmissionMarks]);

  const endSubmission = useCallback(() => {
    setSubmissionMarks({});
  }, [setSubmissionMarks]);

  const rebaseSubmitted = useCallback((submitted: Partial<Record<PricingFieldId, string>>) => {
    const stillSubmitting = { ...submittingRef.current };
    (Object.keys(submitted) as PricingFieldId[]).forEach((id) => {
      delete stillSubmitting[id];
    });
    setSubmissionMarks(stillSubmitting);
    setValues((prev) => {
      const result = {} as Record<PricingFieldId, PricingDraftValue>;
      (Object.keys(prev) as PricingFieldId[]).forEach((id) => {
        const sent = submitted[id];
        const current = prev[id];
        if (sent === undefined) {
          result[id] = current;
        } else if (draftValuesEqual(id, current.value, sent)) {
          result[id] = { value: sent, previous: sent, changed: false, invalid: null };
        } else {
          // Typed after the save was sent: the server holds `sent`, so the new
          // entry is a pending change against it.
          result[id] = { ...current, previous: sent, changed: true };
        }
      });
      initialRef.current = result;
      return result;
    });
  }, [setSubmissionMarks]);

  const dirtyFields = useMemo<PricingFieldId[]>(
    () => (Object.keys(values) as PricingFieldId[]).filter((id) => values[id].changed),
    [values]
  );

  const hasUnsentEdits = useMemo(
    () => (Object.keys(submitting) as PricingFieldId[]).some((id) => {
      const sent = submitting[id];
      return sent !== undefined && !draftValuesEqual(id, values[id].value, sent);
    }),
    [submitting, values]
  );

  const anyInvalid = useMemo(
    () => (Object.keys(values) as PricingFieldId[]).some((id) => values[id].invalid !== null),
    [values]
  );

  const recalcOverrides = useMemo(() => {
    // Per the Phase 2 plan (§ "Blank-handling"): while typing, blank usually
    // means "no override" so live preview falls back to the loan value. Lender
    // credit is the exception: a cleared dollar input should behave like $0 in
    // the math immediately, while save still sends the intentional blank clear.
    const overrides: UsePricingDraftOutput['recalcOverrides'] = {};
    const discount = values.discount_point_percent;
    if (discount.changed && discount.value !== '') {
      overrides.discountPointPercent = parseInputNumber(discount.value);
    }
    const bpc = values.origination_fee_percent;
    if (bpc.changed && bpc.value !== '') {
      overrides.bpcPercent = parseInputNumber(bpc.value);
    }
    const lc = values.lender_credit_4794_dollars;
    if (lc.changed) {
      const rawClean = lc.value.replace(/[$,\s]/g, '');
      const parsed = parseInputNumber(lc.value);
      if (rawClean === '') {
        overrides.lenderCredit4794DollarsOverride = 0;
      } else if (parsed !== null) {
        overrides.lenderCredit4794DollarsOverride = parsed;
      }
    }
    const locr = values.lo_comp_reduction_bps;
    if (locr.changed && locr.value !== '') {
      overrides.locrBps = parseInputNumber(locr.value);
    }
    return overrides;
  }, [values]);

  const buildSavePayload = useCallback<UsePricingDraftOutput['buildSavePayload']>(() => {
    const out: PricingSaveField[] = [];
    (Object.keys(values) as PricingFieldId[]).forEach((id) => {
      if (!values[id].changed) {
        return;
      }
      if (TEXT_FIELD_IDS.has(id)) {
        // Verbatim text: only surrounding whitespace is trimmed. A blank is sent
        // as '' (never null) so the server refuses it explicitly.
        out.push({ fieldId: SAVE_FIELD_ID[id], value: values[id].value.trim() });
        return;
      }
      const rawClean = values[id].value.replace(/[$,\s]/g, '');
      if (rawClean === '') {
        out.push({ fieldId: SAVE_FIELD_ID[id], value: null });
        return;
      }
      const n = toNumberOrNull(rawClean);
      if (n === null) {
        // Unparseable nonblank. The workspace's per-field invalid gate should block Save
        // before we get here, but if it doesn't (or someone bypasses the client), pass the
        // cleaned raw value through so the server's numeric gate can reject it explicitly.
        // Coercing to '0' would silently turn garbage into a legitimate save.
        out.push({ fieldId: SAVE_FIELD_ID[id], value: rawClean });
        return;
      }
      out.push({ fieldId: SAVE_FIELD_ID[id], value: n.toString() });
    });
    return out;
  }, [values]);

  return {
    values,
    setValue,
    setInvalid,
    discardAll,
    discardField,
    rebase,
    rebaseClean,
    rebaseFromValues,
    rebaseSubmitted,
    beginSubmission,
    endSubmission,
    dirtyCount: dirtyFields.length,
    dirtyFields,
    hasUnsentEdits,
    anyInvalid,
    recalcOverrides,
    buildSavePayload
  };
}
